import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchAccessSummary,
  loadCachedAccessSummary,
  clearRbacAccessSummary,
} from "../services";
import {
  evaluatePermission,
  jwtImpliesFullAccess,
} from "../utils/rbacPermissionCheck";

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const [summary, setSummary] = useState(() => loadCachedAccessSummary());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setSummary(null);
      clearRbacAccessSummary();
      return null;
    }
    let userId;
    try {
      userId = JSON.parse(atob(token.split(".")[1]))?.user?.id;
    } catch {
      userId = null;
    }
    if (!userId) {
      setSummary(null);
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAccessSummary(userId);
      setSummary(data);
      return data;
    } catch (e) {
      setError(e);
      setSummary(null);
      clearRbacAccessSummary();
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onLogin = () => refresh();
    window.addEventListener("userLogin", onLogin);
    return () => window.removeEventListener("userLogin", onLogin);
  }, [refresh]);

  const can = useCallback(
    (resource, action) => evaluatePermission(summary, resource, action),
    [summary]
  );

  const canAny = useCallback(
    (checks) => {
      if (!Array.isArray(checks) || !checks.length) return false;
      if (jwtImpliesFullAccess()) return true;
      return checks.some(([r, a]) => can(r, a));
    },
    [can]
  );

  const canAll = useCallback(
    (checks) => {
      if (!Array.isArray(checks) || !checks.length) return false;
      if (jwtImpliesFullAccess()) return true;
      return checks.every(([r, a]) => can(r, a));
    },
    [can]
  );

  const value = useMemo(
    () => ({
      summary,
      loading,
      error,
      refresh,
      can,
      canAny,
      canAll,
      isStaff: summary?.user?.user_type === "staff",
      bypass: !!summary?.fromOwnerBypass || jwtImpliesFullAccess(),
      accessibleStores: summary?.accessibleStores || [],
      accessibleBranches: summary?.accessibleBranches || [],
    }),
    [summary, loading, error, refresh, can, canAny, canAll]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("usePermissionContext must be used within PermissionProvider");
  }
  return ctx;
}
